import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Formik } from 'formik';
import { Fragment, useMemo } from 'react';

import { MarketplaceV1 } from '@jebel/constants';

import {
  CommunityProductFragment,
  CommunitySubscriptionFragment,
  UpdateCommunityProductMutationVariables,
  useUpdateCommunityProductMembershipMutation,
  useUpdateCommunityProductMutation,
} from 'shared/graphql';
import { useMediaDetailer, useToast } from 'shared/hooks';

import {
  CommunityProductForm,
  CommunityProductFormValues,
  DEFAULT_COMMUNITY_PRODUCT_FORM_VALUES,
} from '../../components/CommunityProductForm';

const SAVING_MESSAGE_KEY = 'SAVING_SETTINGS_COMMUNITY_PRODUCTS';

interface Props {
  /**
   * The selected product.
   */
  selectedProduct: CommunityProductFragment & CommunitySubscriptionFragment;

  /**
   * Whether the modal is open.
   */
  isOpen: boolean;

  /**
   * Callback to close the modal.
   */
  onClose: () => void;
}

export function EditCommunityProductModal(props: Props) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();
  const { data: image } = useMediaDetailer(props.selectedProduct.image);

  const [updateProduct] = useUpdateCommunityProductMutation({
    refetchQueries: ['ListCommunityProductMemberships'],
  });

  const [updateMembership] = useUpdateCommunityProductMembershipMutation({
    refetchQueries: ['ListCommunityProducts'],
    awaitRefetchQueries: true,
  });

  const initials = useMemo<CommunityProductFormValues>(() => {
    const { name, description, priceCents, type } = props.selectedProduct;

    const initials: CommunityProductFormValues = {
      name,
      description: description ?? '',
      images: image ? [image] : [],
      priceCents,
      type: type as MarketplaceV1.CommunityProductType,
      color: props.selectedProduct.membership?.color ?? DEFAULT_COMMUNITY_PRODUCT_FORM_VALUES.color,
    };

    if ('frequency' in props.selectedProduct) {
      initials.frequency = props.selectedProduct
        .frequency as MarketplaceV1.CommunitySubscriptionFrequency;
    }

    return initials;
  }, [props.selectedProduct, image]);

  const handleSubmit = async (values: CommunityProductFormValues) => {
    try {
      showMessage(`Saving product, please stand by...`, {
        id: SAVING_MESSAGE_KEY,
      });

      const logo = values.images[0];

      if (props.selectedProduct.membership) {
        const { data: membershipResponse } = await updateMembership({
          variables: {
            id: props.selectedProduct.membership.id,
            community: props.selectedProduct.community!,
            data: {
              name: values.name,
              description: values.description,
              logo: logo?.id,
              color: values.color,
            },
          },
        });

        if (!membershipResponse || !membershipResponse.membership) {
          throw new Error('Failed to update membership');
        }
      }

      const payload: UpdateCommunityProductMutationVariables = {
        id: props.selectedProduct.id!,
        community: props.selectedProduct.community!,
        data: {
          name: values.name,
          description: values.description,
          priceCents: values.priceCents,
          image: logo?.id,
          frequency: values.frequency as MarketplaceV1.CommunitySubscriptionFrequency,
        },
      };

      await updateProduct({ variables: payload });

      showSuccess(`Successfully updated product "${values.name}"`);
      props.onClose();
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(SAVING_MESSAGE_KEY);
    }
  };

  return (
    <Dialog maxWidth="sm" open={props.isOpen} onClose={props.onClose} fullWidth>
      <Formik initialValues={initials} onSubmit={handleSubmit} enableReinitialize>
        {form => (
          <Fragment>
            <DialogTitle>Edit "{form.values.name}"</DialogTitle>

            <DialogContent dividers>
              <CommunityProductForm withPriceChangeWarning />
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" disabled={form.isSubmitting} onClick={props.onClose}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={form.isSubmitting || !form.dirty}
                onClick={form.submitForm}
              >
                Save
              </Button>
            </DialogActions>
          </Fragment>
        )}
      </Formik>
    </Dialog>
  );
}
